import { resolveRadiusNumber } from "@/config/themeTokens";
import { getChartDefaultOptions } from "./ChartJsConfig";
import { useTheme } from "@mui/material/styles";
import appDefaultTheme from "@/config/muiTheme";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as ReactChartJs from "react-chartjs-2";
import {
  formateKeyName,
  getChartColors,
  getFormattedData,
} from "@/helpers/utils";
import TablePaginationBase from "../tables/TablePagination";

type ChartRow = Record<string, string | number | null | undefined>;

type DataFormat = {
  prefix: string;
  suffix: string;
};

type DataFormats = Record<string, DataFormat>;

type DoughnutChartProps = {
  data?: ChartRow[];
  labels?: string[];
  colorSet?: number;
  formats?: DataFormats | null;
};

type ChartRef = unknown;

const { Doughnut } = ReactChartJs as unknown as {
  Doughnut: React.ComponentType<Record<string, unknown>>;
  Chart: {
    pluginService: {
      register: (_plugin: Record<string, unknown>) => void;
    };
  };
};
const TablePagination = TablePaginationBase as React.ComponentType<
  Record<string, unknown>
>;

const legendOptions = {
  display: false,
  position: "right",
};

const DoughnutChart = ({
  data = [],
  labels = [],
  colorSet = 1,
  formats = null,
}: DoughnutChartProps) => {
  const chartRef = useRef<ChartRef>(null);
  const providedTheme = useTheme();
  const theme = providedTheme.palette.neutral ? providedTheme : appDefaultTheme;
  const [chartData, setChartData] = useState<Record<string, unknown>>({});

  // pagination
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const options = useMemo(
    () => ({
      ...getChartDefaultOptions(theme),
      cutoutPercentage: 60,
      aspectRatio: 3,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
      },
      plugins: {
        // labels: {
        //     render: 'value',
        //     fontColor: '#fff'
        // },
        datalabels: {
          color: theme.palette.primary.contrastText,
          font: {
            weight: 600,
            family: theme.typography.fontFamily,
            size: 10,
          },
          formatter: function (value: string | number) {
            const format = formats ? formats[labels[1]] : null;
            if (formats && format) {
              return `${format.prefix}${value}${format.suffix}`;
            }
            return value;
          },
        },
      },
      tooltips: {
        ...getChartDefaultOptions(theme).tooltips,
        cornerRadius: resolveRadiusNumber("radius_container", theme.appTokens),

        callbacks: {
          label: (
            tooltipItem: { index: number },
            data: {
              labels: Array<string | number | null | undefined>;
              datasets: Array<{
                data: Array<string | number | null | undefined>;
              }>;
            }
          ) => {
            const label = data.labels[tooltipItem.index];
            const val = data.datasets[0].data[tooltipItem.index] || "";
            const format = formats ? formats[labels[1]] : null;
            if (formats && format) {
              return `${label}: ${format.prefix}${val}${format.suffix}`;
            }
            return `${label}: ${val}`;
          },
        },
      },
    }),
    [formats, theme]
  );

  const getPageDataIndex = () => {
    const from = (currentPage - 1) * pageSize;
    const to = from + pageSize;
    return { from, to };
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    const total = Math.ceil(data.length / pageSize);
    setTotalPage(total);

    const initData = {
      labels: data
        .slice(getPageDataIndex().from, getPageDataIndex().to)
        .map((d) => d[labels[0]]),
      datasets: [
        {
          data: data.slice(currentPage - 1, pageSize).map((d) => d[labels[1]]),
          backgroundColor: getChartColors(colorSet),
          borderColor: getChartColors(colorSet),
        },
      ],
    };
    setChartData(initData);
  }, [data, theme]);

  useEffect(() => {
    const initData = {
      labels: data
        .slice(getPageDataIndex().from, getPageDataIndex().to)
        .map((d) => d[labels[0]]),
      datasets: [
        {
          data: data
            .slice(getPageDataIndex().from, getPageDataIndex().to)
            .map((d) => d[labels[1]]),
          backgroundColor: getChartColors(colorSet),
          borderColor: getChartColors(colorSet),
        },
      ],
    };
    setChartData(initData);
  }, [currentPage]);

  return (
    <div className="charts doughnut-chart">
      <div className="row pb-2">
        <div className="col-lg-5 text-center px-md-0 ">
          <div className="chart-container">
            <Doughnut
              ref={chartRef}
              data={chartData}
              legend={legendOptions}
              options={options}
              width={250}
              height={250}
              cutoutPercentage="60"
            />
          </div>
        </div>
        <div className="col-lg-7">
          <table
            className="table table-borderless table-hover table-sm"
            style={{ tableLayout: "fixed" }}
          >
            <thead>
              <tr>
                {labels.map((label, i) => (
                  <th
                    className={`text16P_Bold548E8D heading upper ${
                      i % 2 !== 0 ? "text-right" : ""
                    }`}
                    key={`${label}_${i}`}
                  >
                    {formateKeyName(label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data
                .slice(getPageDataIndex().from, getPageDataIndex().to)
                .map((d, i) => (
                  <tr key={`${d[labels[0]]}_${i}`}>
                    {Object.keys(d).map((key, j) => (
                      <td
                        className={`text16P_Reg626262 text-truncate ${
                          j % 2 !== 0 ? "text-right" : ""
                        }`}
                        key={`${key}_${i}_${j}`}
                      >
                        {getFormattedData(d[key], key, formats)}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-12 float-right">
          <TablePagination
            page={currentPage}
            count={totalPage}
            onChange={handlePageChange}
            className="float-right"
          />
        </div>
      </div>
    </div>
  );
};

export default DoughnutChart;
